// Imports
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import MultiFieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import { visuallyHiddenLabelStyle } from '../../../../shared-components/visually-hidden-label';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import usePagination from '../../../../../../shareables/foundation/front-end/utils/use-pagination';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetAccountsResponse } from '../../../../../../shareables/types/api/main/website/page/type/207/accounts/get';
import Account from './account';


// Styled components
const SearchAndPageSizeWrapper = styled(MultiFieldContainer)`
	label {
		${visuallyHiddenLabelStyle}
	}
	
	margin-bottom: 0.8438rem;
`;

const Heading = styled.h3`
	display: block;
	font-size: 1.5rem;
	margin-top: 1.25rem;
	margin-bottom: 0.5313rem;
	font-weight: 500;
	line-height: 1.1;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookAccounts?: GetAccountsResponse;
	storybookSearchQuery?: string;
}


// Function component
const ResidentDirectory: React.FC<Props> = ({ pageID, storybookAccounts, storybookSearchQuery }) => {
	// Use state
	const [allAccounts, setAllAccounts] = useState<GetAccountsResponse | null>(storybookAccounts ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Create search field
	const {
		items: filteredAccounts,
		setItems: setFilteredAccounts,
		searchQuery,
		searchField,
		pageSizeField,
		paginationComponent,
	} = usePagination<GetAccountsResponse[0]>({
		cacheStorageKey: `resident-directory-${pageID}-accounts`,
		pluralDescriptor: 'accounts',
		pageSizes: [50, 100, 250, 500],
		searchableKeys: [
			(account) => account.addresses.map((address) => address.formatted).join(' ') || 'no addresses',
			(account) => account.consolidatedName,
		],
		initialItems: storybookAccounts,
		initialSearchQuery: storybookSearchQuery,
	});
	
	
	// Load accounts on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetAccountsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/207/accounts`,
				completion: (json) => {
					setAllAccounts(json.response);
					setFilteredAccounts(json.response);
				},
			})
		);
	}, [dispatch, pageID, setFilteredAccounts]);
	
	
	// If loading, display nothing
	if (allAccounts === null) {
		return null;
	}
	
	
	// Add placeholder to search field
	const searchFieldWithPlaceholder = React.cloneElement(searchField, { placeholder: 'Search residents…' });
	
	
	// Return JSX
	return (
		<React.Fragment>
			<SearchAndPageSizeWrapper centerOnMobile grow={1} moreSpaceBetween>
				{searchFieldWithPlaceholder}
				{pageSizeField}
			</SearchAndPageSizeWrapper>
			
			{allAccounts.length === 0 && <em>No accounts have chosen to be listed in the directory yet…</em>}
			
			{searchQuery.length > 0 && allAccounts.length > 0 && (
				<React.Fragment>
					<Heading>Showing results for “{searchQuery}”</Heading>
					{filteredAccounts?.length === 0 && <em>No results found…</em>}
				</React.Fragment>
			)}
			
			{filteredAccounts?.map((account, i) => <Account key={i} account={account} />)}
			
			{paginationComponent}
		</React.Fragment>
	);
};

export default ResidentDirectory;
